/**
 * Ciclo de flush: seleção de lotes, delivery e retry.
 */

import { getFingerprint } from '../../utils/getFingerprint';
import { getNetworkOnline } from '../network';
import { isCircuitOpen } from '../circuitBreaker';
import { getTabId } from '../tabCoordinator';
import {
  recordFailedBatch,
  recordFlushDuration,
  recordQueueSize,
  recordEventPhase,
  updatePartitionSizes,
} from '../telemetry';
import { recoverStuckProcessingItems } from '../recovery/processingRecovery';
import { resyncQueueIndexFromArray } from './queueState';
import { transitionStatus } from './stateMachine';
import { computeMemoryPressure } from '../performance/memoryPressure';
import {
  evaluateFlushGate,
  releaseFlushCoordination,
  renewFlushCoordination,
  assertFlushCoordinationValid,
  startFlushHeartbeat,
  stopFlushHeartbeat,
} from './coordinationEngine';
import { deliverBatch } from './deliveryEngine';
import { recordReplayLineage } from '../replay/replayLineage';
import { groupEligibleBySession, selectReplayBatchesForGroup } from './replayEngine';
import { applyPartitionFlushCaps } from './partitionPolicies';
import {
  applyBatchResponse,
  applyNetworkFailureRetry,
  getEligibleForFlush,
  pruneQueueBeforeFlush,
} from './retryEngine';
import {
  findQueueIndex,
  getMaxQueueItemsLimit,
  getQueueMutable,
  persistQueue,
} from './queueState';
import type { QueueItem } from './types';
import { getPartitionSizes } from './partitioning';
import { setLastReplayedSequenceSync } from '../replay/persistentReplay';
import { getSettledEventIds } from '../batchResponse';
import {
  getAdaptiveMaxBatchesPerCycle,
  markFlushCompleted,
} from './flushBudget';
import {
  beginReplaySession,
  completeReplaySession,
  failReplaySession,
  assertReplaySessionValid,
} from '../replay/replaySession';
import { scheduleReplayRecovery } from '../replay/replayRecovery';
import { reconcileAllSessionCursors } from '../replay/replayCursorReconciliation';
import { recordPartitionFlushLatency } from '../telemetry';
import { updateRuntimeMode } from '../runtime/runtimeMode';
import {
  captureReplaySnapshot,
  rollbackReplaySnapshot,
} from '../replay/replaySnapshot';
import { getQueuePartition } from './partitioning';
import {
  acquireOperationContext,
  assertOperationValid,
  rejectIfInvalid,
} from '../coordination/ownershipGate';
import { recordLockConflict } from '../telemetry';

function markBatchProcessing(batchItems: QueueItem[], now: number): void {
  const tabId = getTabId();
  for (const item of batchItems) {
    const idx = findQueueIndex(item.id);
    if (idx < 0) continue;
    const queue = getQueueMutable();
    const current = queue[idx];
    if (current.status === 'processing') continue;
    try {
      queue[idx].status = transitionStatus(current.status, 'processing');
    } catch {
      continue;
    }
    queue[idx].processingAt = now;
    queue[idx].processingTabId = tabId;
    recordEventPhase(item.id, 'processing');
  }
}

function recordReplayedSequences(
  batchItems: QueueItem[],
  settledIds: Set<string>,
  batchId: string
): void {
  for (const item of batchItems) {
    if (!settledIds.has(item.id)) continue;
    setLastReplayedSequenceSync(item.params.sessionId, item.sessionSequence, {
      eventId: item.id,
      partition: getQueuePartition(item),
      batchId,
    });
  }
}

export async function runFlushCycle(): Promise<void> {
  const gate = evaluateFlushGate();
  if (!gate.allowed) {
    persistQueue();
    return;
  }

  startFlushHeartbeat();
  const opCtx = acquireOperationContext('flush');
  const ownershipGate = rejectIfInvalid(opCtx);
  if (!ownershipGate.ok) {
    if (
      ownershipGate.reason === 'fencing_invalid' ||
      ownershipGate.reason === 'owner_invalidated'
    ) {
      recordLockConflict();
    }
    persistQueue();
    stopFlushHeartbeat();
    releaseFlushCoordination();
    return;
  }

  try {
    updateRuntimeMode();
    const now = Date.now();
    const reconcile = reconcileAllSessionCursors(getQueueMutable());
    if (reconcile.some(r => r.action === 'recovery')) {
      scheduleReplayRecovery();
    }
    recoverStuckProcessingItems(getQueueMutable(), now);
    resyncQueueIndexFromArray();
    pruneQueueBeforeFlush();

    let eligible = getEligibleForFlush(now);
    updatePartitionSizes(getPartitionSizes(eligible));

    const capStart = Date.now();
    eligible = applyPartitionFlushCaps(eligible, 40);
    recordPartitionFlushLatency('all', Date.now() - capStart);

    if (eligible.length === 0) {
      persistQueue();
      return;
    }

    const fingerprint = await getFingerprint();
    const byGroup = groupEligibleBySession(eligible);
    const pressure = computeMemoryPressure(
      getQueueMutable(),
      getMaxQueueItemsLimit()
    );
    const flushStart = Date.now();
    let batchesSent = 0;
    const maxBatches = getAdaptiveMaxBatchesPerCycle();
    let snapshotId: string | null = null;
    let sessionStarted = false;

    for (const [, groupItems] of byGroup) {
      if (batchesSent >= maxBatches) break;
      const batchGroups = selectReplayBatchesForGroup(groupItems, {
        queuePressure: pressure.level,
        networkOnline: getNetworkOnline(),
        circuitOpen: isCircuitOpen(),
      });

      for (const batchItems of batchGroups) {
        if (batchesSent >= maxBatches) break;
        if (batchItems.length === 0) continue;
        const hasCritical = batchItems.some(
          i =>
            getQueuePartition(i) === 'critical' ||
            getQueuePartition(i) === 'conversion'
        );
        if (hasCritical && !snapshotId) {
          snapshotId = captureReplaySnapshot(getQueueMutable());
        }
        if (!sessionStarted) {
          beginReplaySession(batchItems.map(i => i.id), {
            snapshotId: snapshotId ?? undefined,
          });
          sessionStarted = true;
        }
        renewFlushCoordination();
        if (
          !assertFlushCoordinationValid() ||
          !assertOperationValid(opCtx) ||
          !assertReplaySessionValid()
        ) {
          continue;
        }
        markBatchProcessing(batchItems, now);
        const batchId = `batch_${Date.now()}_${batchesSent}`;

        try {
          const response = await deliverBatch(batchItems, fingerprint);
          batchesSent++;
          const batchIds = batchItems.map(i => i.id);
          const settled = getSettledEventIds(response, batchIds, true);
          applyBatchResponse(batchItems, response, now);
          recordReplayedSequences(batchItems, settled, batchId);
          recordReplayLineage(batchItems);
        } catch {
          recordFailedBatch();
          failReplaySession();
          if (snapshotId) {
            rollbackReplaySnapshot(snapshotId, getQueueMutable());
          }
          scheduleReplayRecovery();
          applyNetworkFailureRetry(batchItems, now);
        }
      }
    }

    if (sessionStarted) completeReplaySession();
    recordFlushDuration(Date.now() - flushStart);
    recordQueueSize(getQueueMutable().length);
    persistQueue();
    markFlushCompleted();
  } finally {
    stopFlushHeartbeat();
    releaseFlushCoordination();
  }
}

